import {
  BackgroundColorProps,
  ColSpanProp,
  FlexJustifyProp,
  GapProp,
  GrowProp,
  GroupedProp,
  BorderProps,
  InputColorProps,
  TextAlignProp,
  TextColorProps,
  TextFontProps,
  TextWeightProp,
  SizeProp,
  VisibilityProp,
  WidthProp,
} from "./commonProps";

export function backgroundColorClassName({ primary, white, neutral, yellow }: BackgroundColorProps) {
  switch (true) {
    case primary:
      return "bg-primary-100 dark:bg-primary-200";
    case neutral:
      return "bg-neutral-102 dark:bg-neutral-200";
    case yellow:
      return "bg-yellow-100 dark:bg-yellow-100";
    case white:
    default:
      return "bg-white dark:bg-neutral-800";
  }
}

export function widthClassName({ width }: WidthProp) {
  switch (width) {
    case 24:
      return "w-24";
    case 28:
      return "w-28";
    case 32:
      return "w-32";
    case 36:
      return "w-36";
    case 40:
      return "w-full md:w-40";
    case 44:
      return "w-full md:w-44";
    case 48:
      return "w-full md:w-48";
    case 52:
      return "w-full md:w-52";
    case 56:
      return "w-full md:w-56";
    case 60:
      return "w-full md:w-60";
    case 64:
      return "w-full md:w-64";
    case 72:
      return "w-full md:w-72";
    case 80:
      return "w-full md:w-80";
    case 96:
      return "w-full md:w-96";
    case "w-full":
      return "w-full";
    case "w-1/2":
      return "w-1/2";
    case "w-1/3":
      return "w-1/3";
    case "w-11":
      return "w-11";
    case "w-20":
      return "w-20";
    default:
      return "";
  }
}

/*
Gap resizes on medium breakpoint. For example

    "gap-6 md:gap-12"

Smaller gaps do not resize, for example

    "gap-1"
*/
export function gapClassName({ gap }: GapProp) {
  switch (gap) {
    case 16:
      return "gap-6 md:gap-16";
    case 12:
      return "gap-6 md:gap-12";
    case 11:
      return "gap-6 md:gap-11";
    case 10:
      return "gap-5 md:gap-10";
    case 9:
      return "gap-5 md:gap-9";
    case 8:
      return "gap-4 md:gap-8";
    case 7:
      return "gap-3.5 md:gap-7";
    case 6:
      return "gap-3 md:gap-6";
    case 5:
      return "gap-2.5 md:gap-5";
    case 4:
      return "gap-2 md:gap-4";
    case 3.5:
      return "gap-2 md:gap-3.5";
    case 3:
      return "gap-2 md:gap-3";
    case 2.5:
      return "gap-2 md:gap-2.5";
    case 2:
      return "gap-2";
    case 1.5:
      return "gap-1.5";
    case 1:
      return "gap-1";
    case 0.75:
      return "gap-0 md:gap-8";
    case 0.5:
      return "gap-0.5";
    case 0:
      return "gap-0";
    default:
      return "";
  }
}

export function roundedClassName({ grouped }: GroupedProp) {
  return grouped
    ? [
        "first:rounded-tl-md first:rounded-tr-none first:rounded-bl-md first:rounded-br-none",
        "last:rounded-tl-none last:rounded-tr-md last:rounded-bl-none last:rounded-br-md",
      ].join(" ")
    : "rounded-md";
}

export function growClassName({ grow }: GrowProp) {
  switch (grow) {
    case 0:
      return "grow-0";
    case 1:
      return "grow";
    default:
      return "";
  }
}

export function flexJustifyClassName({ justify }: FlexJustifyProp) {
  switch (justify) {
    case "start":
      return "justify-start";
    case "end":
      return "justify-end";
    case "center":
      return "justify-center";
    case "between":
      return "justify-between";
    case "around":
      return "justify-around";
    case "evenly":
      return "justify-evenly";
    default:
      return "";
  }
}

export function colSpanClassName({ colSpan }: ColSpanProp) {
  switch (colSpan) {
    case 1:
      return "col-span-1";
    case 2:
      return "col-span-1 md:col-span-2";
    case 3:
      return "col-span-1 md:col-span-3";
    case 4:
      return "col-span-1 md:col-span-4";
    default:
      return "";
  }
}

export function inputClassName() {
  return ["bg-clip-padding outline-none"].join(" ");
}

export function borderClassName({ border, grouped }: BorderProps) {
  switch (true) {
    case grouped && border === "neutral":
      return [
        "border-b border-b-neutral-120",
        "border-t border-t-neutral-120",
        "first:border-l first:border-l-neutral-120",
        "last:border-r last:border-r-neutral-120",
      ].join(" ");
    case border === "neutral":
      return "border border-neutral-120";
    case border === "green":
      return "border border-green-500";
    case border === "red":
      return "border border-red-500";
    case border === "primary":
      return "border border-primary-500";
    case border === "yellow":
      return "border border-yellow-500";
    case border === "borderDashed":
      return "border border-dashed";
    default:
      return "border border-transparent";
  }
}

export function inputColorClassName({ neutral, transparent }: InputColorProps) {
  switch (true) {
    case neutral:
      return "bg-neutral-100";
    case transparent:
      return "bg-transparent";
    default:
      return "bg-white dark:bg-neutral-800";
  }
}

export function getOutlineColor({ border }: BorderProps) {
  switch (border) {
    case "neutral":
      return "outline-blue-500";
    case "green":
      return "outline-green-500";
    case "red":
      return "outline-red-500";
    case "primary":
      return "outline-primary-500";
    case "yellow":
      return "outline-yellow-500";
    default:
      return "outline-transparent";
  }
}

export function getFocusOutline({ border }: BorderProps) {
  return `focus:outline-none focus:ring focus:${getOutlineColor({ border })}`;
}

export const textColorPrimaryClassName = "text-primary-500";

export function textColorClassName({
  currentColor,
  transparent,
  neutral,
  black,
  accent,
  primary,
  yellow,
  white,
  green,
  red,
}: TextColorProps) {
  switch (true) {
    case currentColor:
      return "text-current";
    case transparent:
      return "text-transparent";
    case accent:
      return "text-neutral-400 dark:text-white";
    case neutral:
      return "text-neutral-110 dark:text-neutral-800";
    case primary:
      return textColorPrimaryClassName;
    case green:
      return "text-green-500";
    case red:
      return "text-red-500";
    case yellow:
      return "text-yellow-500";
    case black:
      return "text-black";
    case white:
      return "text-white";
    default:
      return "";
  }
}

export function sizeClassName({ size }: SizeProp) {
  switch (size) {
    case "small":
      return "h-8 py-1 px-3 text-sm";
    case "large":
      return "h-14 py-3 px-5 text-xl";
    case "medium":
      return "h-12 py-2 px-8 text-xl";
    default:
      return "h-12 py-2 px-3 text-base";
  }
}

export function btnSizeClassName({ size }: SizeProp) {
  switch (size) {
    case "small":
      return "h-8 py-1 px-2 text-sm";
    case "large":
      return "h-14 py-3 px-4 text-xl";
    case "medium":
      return "h-11 py-2.5 px-5 text-base";
    default:
      return "h-12 py-2 px-3 text-base";
  }
}

export function textSizeClassName({ size }: Pick<TextFontProps, "size">) {
  switch (size) {
    case "no-padd":
      return "px-0 text-base";
    case "xs":
      return "text-xs";
    case "sm":
      return "text-sm";
    case "lg":
      return "text-lg";
    case "xl":
      return "text-xl";
    case "2xl":
      return "text-2xl";
    case "3xl":
      return "text-3xl";
    case "4xl":
      return "text-4xl";
    case "5xl":
      return "text-5xl";
    case "6xl":
      return "text-6xl";
    case "7xl":
      return "text-7xl";
    case "8xl":
      return "text-8xl";
    case "9xl":
      return "text-9xl";
    case "base":
      return "px-3 py-2.5 text-base";
    default:
      return "";
  }
}

export function textAlignClassName({ textAlign }: TextAlignProp) {
  switch (textAlign) {
    case "left":
      return "text-left";
    case "right":
      return "text-right";
    case "center":
      return "text-center";
    default:
      return "";
  }
}

export function textLineHeightClassName({ lineHeight }: Pick<TextFontProps, "lineHeight">) {
  switch (lineHeight) {
    case 3:
      return "leading-3";
    case 4:
      return "leading-4";
    case 5:
      return "leading-5";
    case 6:
      return "leading-6";
    case 7:
      return "leading-7";
    case 8:
      return "leading-8";
    case 9:
      return "leading-9";
    case 10:
      return "leading-10";
    case 11:
      return "leading-11";
    case 12:
      return "leading-12";
    default:
      return "";
  }
}

function textWeightClassName({ font }: TextWeightProp) {
  switch (font) {
    case "light":
      return "font-light font-poppins";
    case "normal":
      return "font-normal font-poppins";
    case "medium":
      return "font-medium font-poppins";
    case "semibold":
      return "font-semibold font-poppins";
    case "bold":
      return "font-bold font-poppins";
    case "extrabold":
      return "font-extrabold font-poppins";
    case "black":
      return "font-black font-poppins";
    default:
      return "";
  }
}

export function textFontClassName({ font, lineHeight, size, uppercase, underline }: TextFontProps) {
  return [
    underline ? "underline" : "",
    uppercase ? "uppercase" : "",
    textWeightClassName({ font }),
    textSizeClassName({ size }),
    textLineHeightClassName({ lineHeight }),
  ].join(" ");
}

type TransitionClassNameArg = {
  all?: boolean;
  colors?: boolean;
  opacity?: boolean;
};

export function transitionClassName({ all, colors, opacity }: TransitionClassNameArg) {
  const reduceMotion = "motion-reduce:transition-none";

  if (all) {
    return `transition-all ${reduceMotion}`;
  }

  return [colors ? "transition-colors" : "", opacity ? "transition-opacity" : "", reduceMotion].join(" ");
}

export const pageMargin = "px-2 lg:px-4 xl:px-24";

export function visibilityClassName({ invisible }: VisibilityProp) {
  return invisible ? "invisible" : "";
}
